/**
 * Permission evaluation core.
 *
 * Pure functions with every input passed in, so both catalogues can be unit-tested without
 * React, the stores or `import.meta.env`. `permissions.ts` binds these to the live sources.
 *
 * v1 keeps the hard-coded role tables in `permission.config.ts`; v2 checks the permission
 * name against the grants of the single role selected at `/additional-info`. In v1 a call site
 * still passes the v2 name, which is mapped back to the legacy key it used before the sweep,
 * so v1 behaviour is unchanged.
 */

import {
  PREMISSIONS_ACTIONS_BY_ROLE,
  PREMISSIONS_BY_ROLE,
  toLegacyActionKey,
  toLegacyPageKey,
  toV2PermissionName,
} from "./permission.config.ts";

export type PermissionByRole = {
  label: string;
  value: string;
};

/**
 * The role list of one navigation entry. It carries the page key it was built from so the v2
 * catalogue can resolve the entry's permission name; v1 only ever reads the array itself.
 */
export type PermissionRoleList = PermissionByRole[] & { pageKey?: string };

export type PermissionCatalogue = "v1" | "v2";

/** Loading state of the selected role's permissions. */
export type RolePermissionsStatus = "idle" | "loading" | "ready" | "error";

export interface PermissionContext {
  /** Which catalogue is in force. */
  catalogue: PermissionCatalogue;
  /** Debug kill switch: when false every check grants access. */
  permissionSystemEnabled: boolean;
  /** Roles the session runs under (exactly one in practice). */
  userRoles: PermissionByRole[];
  /** v2 permission names granted to the selected role. */
  rolePermissions: string[];
}

const matchesUserRole = (
  allowedRoles: readonly PermissionByRole[],
  userRoles: readonly PermissionByRole[],
): boolean =>
  userRoles.some((userRole) => allowedRoles.some((allowed) => userRole.label === allowed.label));

const rolesForPageKey = (key: string): readonly PermissionByRole[] | undefined =>
  PREMISSIONS_BY_ROLE[toLegacyPageKey(key) as keyof typeof PREMISSIONS_BY_ROLE];

const grantsPermission = (context: PermissionContext, permission: string): boolean =>
  context.rolePermissions.includes(toV2PermissionName(permission));

/**
 * Check a page, sidebar entry or route permission.
 *
 * @param permission v2 permission name, or the legacy page key it replaced
 * @param context Catalogue, kill switch and the session's roles/permissions
 * @returns Whether the page may be reached
 */
export const evaluatePagePermission = (permission: string, context: PermissionContext): boolean => {
  if (!context.permissionSystemEnabled) return true;
  if (context.catalogue === "v2") return grantsPermission(context, permission);

  const allowedRoles = rolesForPageKey(permission);
  if (!allowedRoles || allowedRoles.length === 0) return false;
  return matchesUserRole(allowedRoles, context.userRoles);
};

/**
 * Check a button or field level permission.
 *
 * The v1 branch reproduces the original lookup order exactly: the `PREMISSIONS_BY_ROLE` table
 * first (that is where the `Consultation.*` constants live), then the per-role action lists.
 *
 * @param permission v2 permission name, or the legacy action key it replaced
 * @param context Catalogue, kill switch and the session's roles/permissions
 * @returns Whether the action is allowed
 */
export const evaluateActionPermission = (
  permission: string,
  context: PermissionContext,
): boolean => {
  if (!context.permissionSystemEnabled) return true;
  if (context.catalogue === "v2") return grantsPermission(context, permission);

  if (context.userRoles.length === 0) return false;

  const legacyKey = toLegacyActionKey(permission);
  const allowedRoles = PREMISSIONS_BY_ROLE[legacyKey as keyof typeof PREMISSIONS_BY_ROLE];
  if (allowedRoles && allowedRoles.length > 0) {
    return matchesUserRole(allowedRoles, context.userRoles);
  }

  return context.userRoles.some((userRole) => {
    if (!userRole.label) return false;
    const roleActions =
      PREMISSIONS_ACTIONS_BY_ROLE[userRole.label as keyof typeof PREMISSIONS_ACTIONS_BY_ROLE] ?? [];
    return roleActions.includes(legacyKey);
  });
};

/**
 * Check a navigation entry's role list.
 *
 * An entry built by `getPermissionsByRole` carries its page key and is resolved by permission
 * name under v2. An inline role list (no page key) has no permission name to check, so it
 * keeps the role comparison in both catalogues.
 *
 * @param requiredPermissions Role list, single role, or an empty list meaning "no restriction"
 * @param context Catalogue, kill switch and the session's roles/permissions
 * @returns Whether the entry is visible/reachable
 */
export const evaluateRoleListPermission = (
  requiredPermissions: PermissionRoleList | PermissionByRole | PermissionByRole[] | null,
  context: PermissionContext,
): boolean => {
  if (!context.permissionSystemEnabled) return true;

  if (
    requiredPermissions == null ||
    (Array.isArray(requiredPermissions) && requiredPermissions.length === 0)
  ) {
    return true;
  }

  const pageKey = Array.isArray(requiredPermissions)
    ? (requiredPermissions as PermissionRoleList).pageKey
    : undefined;

  if (context.catalogue === "v2" && pageKey) {
    return grantsPermission(context, pageKey);
  }

  const allowedRoles = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];
  return matchesUserRole(allowedRoles, context.userRoles);
};

/** What a route guard should render. */
export type GuardDecision = "loading" | "select-role" | "denied" | "allowed";

export interface GuardStateInput {
  catalogue: PermissionCatalogue;
  /** Whether the persisted additional-info store has rehydrated. */
  hasHydrated: boolean;
  /** Whether a role has been selected at `/additional-info`. */
  hasRole: boolean;
  /** Load state of the selected role's permissions (v2 only). */
  rolePermissionsStatus: RolePermissionsStatus;
  /** Result of the page permission check. */
  hasAccess: boolean;
}

/**
 * Decide what a permission-guarded route renders.
 *
 * v2 additionally waits for the selected role's permissions, because an empty list denies
 * everything and would otherwise bounce the user off a page they may open.
 *
 * @param input Hydration, role selection, permission load state and the access result
 * @returns The decision the guard should act on
 */
export const evaluateGuardState = (input: GuardStateInput): GuardDecision => {
  if (!input.hasHydrated) return "loading";
  if (!input.hasRole) return "select-role";

  if (input.catalogue === "v2") {
    const isLoadingPermissions =
      input.rolePermissionsStatus === "idle" || input.rolePermissionsStatus === "loading";
    if (isLoadingPermissions) return "loading";
  }

  return input.hasAccess ? "allowed" : "denied";
};
